import React, { useEffect, useRef, useState } from "react";
import { Save, Download, Upload, RotateCcw, Trash2, Loader2, HardDriveDownload, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function SaveFiles({ gameId, config, title, onRestore, refreshKey }) {
  const [saves, setSaves] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/games/${gameId}/saves`);
      setSaves(data.saves || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [gameId, refreshKey]); // eslint-disable-line

  const saveCurrent = async () => {
    setSaving(true);
    try {
      await api.post(`/games/${gameId}/saves`, { name: name.trim() || `Save ${saves.length + 1}` });
      setName("");
      toast.success("Save file created 💾");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const restore = async (sid) => {
    setBusy(sid);
    try {
      const { data } = await api.post(`/games/${gameId}/saves/${sid}/restore`);
      if (data.config && onRestore) onRestore(data.config);
      toast.success("Save restored ✅");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Restore failed"); }
    finally { setBusy(""); }
  };

  const del = async (sid) => {
    try {
      await api.delete(`/games/${gameId}/saves/${sid}`);
      setSaves((s) => s.filter((x) => x.id !== sid));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadProject = () => {
    if (!config) return;
    const payload = { _gameview_project: 1, title: title || "My Game", exported_at: new Date().toISOString(), config };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (title || "game").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
    a.href = url; a.download = `${safe || "game"}.gvproj.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Project file downloaded ⬇️");
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setBusy("import");
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const cfg = parsed.config || parsed;
      const { data } = await api.post(`/games/${gameId}/import`, { config: cfg });
      if (data.config && onRestore) onRestore(data.config);
      toast.success("Project imported 🎉");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "That file couldn't be imported");
    } finally { setBusy(""); }
  };

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] mt-4 p-4" data-testid="save-files-panel">
      <div className="flex items-center gap-2 font-bold text-sm mb-3"><HardDriveDownload className="w-4 h-4" /> Save Files</div>

      <div className="flex gap-1.5 mb-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this save (optional)"
          className="h-8 text-sm" data-testid="save-name-input" onKeyDown={(e) => e.key === "Enter" && saveCurrent()} />
        <Button size="sm" onClick={saveCurrent} disabled={saving} className="rounded-full shrink-0" data-testid="save-current-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </Button>
      </div>

      <div className="space-y-1.5 max-h-40 overflow-y-auto mb-3" data-testid="saves-list">
        {saves.length === 0 ? (
          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">No save files yet — hit Save to snapshot your world so you can always come back to it.</p>
        ) : saves.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 text-sm rounded-lg px-2 py-1 hover:bg-[hsl(var(--secondary))]/40" data-testid={`save-item-${s.id}`}>
            <div className="min-w-0 flex items-center gap-1.5">
              <span className="shrink-0">{s.cover_emoji || "🎮"}</span>
              <span className="font-medium truncate">{s.name}</span>
              {s.auto && <span className="shrink-0 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] flex items-center gap-0.5" title="Auto-saved before an AI change"><Sparkles className="w-2.5 h-2.5" />auto</span>}
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => restore(s.id)} disabled={busy === s.id} title="Restore" data-testid={`restore-save-${s.id}`}>
                {busy === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-[hsl(var(--muted-foreground))] hover:text-rose-400" onClick={() => del(s.id)} title="Delete" data-testid={`delete-save-${s.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-1.5 pt-2 border-t border-[hsl(var(--border))]">
        <Button variant="outline" size="sm" onClick={downloadProject} className="rounded-full flex-1" data-testid="download-project-btn">
          <Download className="w-3.5 h-3.5" /> Download
        </Button>
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy === "import"} className="rounded-full flex-1" data-testid="import-project-btn">
          {busy === "import" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Import
        </Button>
        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onImportFile} data-testid="import-project-input" />
      </div>
    </div>
  );
}
